/**
 * Open fonts with the same glyph metrics as a proprietary face a quote may name. A line of text then breaks at the same word and a
 * page at the same line in the preview and in the PDF, on every operating system and in a deployed build, without any font file of
 * the proprietary face being shipped or read from the machine.
 *
 * Both renderers read this one list. The preview declares an `@font-face` for each substitute and puts it right after the requested
 * family in its font stack (so a machine that has the real face still uses it, with the same metrics). The PDF registers the same
 * files for the entries marked `inPdf`; the others (Arial, Times New Roman, Courier New) print in the PDF's built-in Helvetica, Times
 * and Courier, which already have those metrics, so only the preview needs an open face, for a machine such as a Linux desktop that
 * has none of the Microsoft families. The files come from a CDN the PDF already uses for Inter, pinned to one version, and are not
 * copied into the repo.
 *
 * Add a family only when it has a metric-compatible substitute under an open licence. Aptos and Georgia have none and keep the
 * existing fallback.
 */
export interface MetricCompatibleFace {
  src: string;
  fontWeight: 400 | 700;
  fontStyle?: 'italic';
}

export interface MetricCompatibleFont {
  /** The family a block asks for. */
  family: string;
  /** The open family that stands in for it. */
  substitute: string;
  /** SPDX identifier of the substitute's licence. */
  license: string;
  /** Whether the PDF registers the substitute (otherwise the PDF's built-in face already has the metrics). */
  inPdf: boolean;
  faces: readonly MetricCompatibleFace[];
}

const FONTSOURCE_BASE_URL = 'https://cdn.jsdelivr.net/npm/@fontsource';
/** One fontsource version for every preview-only face (the same one the PDF pins for Inter). */
const FONTSOURCE_VERSION = '5.2.5';

/** Latin subset faces, small enough for the preview of a machine that lacks the proprietary family. */
function fontsourceFaces(packageName: string): MetricCompatibleFace[] {
  const file = (weight: 400 | 700, style: 'normal' | 'italic'): MetricCompatibleFace => ({
    src: `${FONTSOURCE_BASE_URL}/${packageName}@${FONTSOURCE_VERSION}/files/${packageName}-latin-${weight}-${style}.woff`,
    fontWeight: weight,
    ...(style === 'italic' ? { fontStyle: 'italic' as const } : {})
  });
  return [file(400, 'normal'), file(700, 'normal'), file(400, 'italic'), file(700, 'italic')];
}

/**
 * Carlito as the complete font files (Latin, Latin Extended, Cyrillic and Greek in one file), from the google/fonts repository at one
 * commit. react-pdf cannot fall back from one font file to another for a missing glyph, so the PDF needs a file that has them all;
 * the preview reads the same files.
 */
const GOOGLE_FONTS_COMMIT = '3dd78844021e948ceb633d1dcee3f7885561b5d9';

function carlitoFaces(): MetricCompatibleFace[] {
  const file = (name: string, weight: 400 | 700, italic: boolean): MetricCompatibleFace => ({
    src: `https://cdn.jsdelivr.net/gh/google/fonts@${GOOGLE_FONTS_COMMIT}/ofl/carlito/Carlito-${name}.ttf`,
    fontWeight: weight,
    ...(italic ? { fontStyle: 'italic' as const } : {})
  });
  return [file('Regular', 400, false), file('Bold', 700, false), file('Italic', 400, true), file('BoldItalic', 700, true)];
}

export const METRIC_COMPATIBLE_FONTS: readonly MetricCompatibleFont[] = [
  { family: 'Calibri', substitute: 'Carlito', license: 'OFL-1.1', inPdf: true, faces: carlitoFaces() },
  { family: 'Arial', substitute: 'Arimo', license: 'OFL-1.1', inPdf: false, faces: fontsourceFaces('arimo') },
  { family: 'Times New Roman', substitute: 'Tinos', license: 'OFL-1.1', inPdf: false, faces: fontsourceFaces('tinos') },
  { family: 'Courier New', substitute: 'Cousine', license: 'OFL-1.1', inPdf: false, faces: fontsourceFaces('cousine') }
];

export function findMetricCompatibleFont(requested: string | null | undefined): MetricCompatibleFont | undefined {
  const normalized = requested?.trim().toLowerCase() ?? '';
  if (!normalized) return undefined;
  return METRIC_COMPATIBLE_FONTS.find((font) => font.family.toLowerCase() === normalized);
}

/** The stand-ins the PDF registers. */
export const PDF_METRIC_COMPATIBLE_FONTS: readonly MetricCompatibleFont[] = METRIC_COMPATIBLE_FONTS.filter((font) => font.inPdf);

function fontFormat(src: string): string {
  return src.endsWith('.ttf') ? 'truetype' : 'woff';
}

/** `@font-face` rules for the preview. A face is downloaded only when text that uses the substitute is drawn. */
export function metricCompatibleFontFaceCss(fonts: readonly MetricCompatibleFont[] = METRIC_COMPATIBLE_FONTS): string {
  return fonts
    .flatMap((font) =>
      font.faces.map((face) => {
        const style = face.fontStyle ?? 'normal';
        return `@font-face{font-family:${JSON.stringify(font.substitute)};src:url(${JSON.stringify(face.src)}) format("${fontFormat(face.src)}");font-weight:${face.fontWeight};font-style:${style};font-display:swap;}`;
      })
    )
    .join('');
}
